/**
 * Uncertainty can be either a legacy single number or the new dict format.
 */
export interface UncertaintyDetail {
    std?: number | null;
    mean?: number | null;
    median?: number | null;
    ci95_low?: number | null;
    ci95_high?: number | null;
    cv_percent?: number | null;
    n_valid?: number | null;
}

export type UncertaintyInput = number | UncertaintyDetail | null | undefined;

/**
 * Format a predicted concentration as "value ± cv% IU/L" or "value ± std IU/L".
 * - Falls back to "—" when value is missing or not finite.
 * - Uses cv_percent from the dict when available.
 * - Falls back to std / single-number otherwise.
 */
export function formatConcentration(value?: number | null, uncertainty?: UncertaintyInput): string {
    if (value === undefined || value === null || !Number.isFinite(value)) {
        return '—';
    }

    const formatted = value.toFixed(2);

    // Dict-based uncertainty: prefer cv_percent
    if (uncertainty != null && typeof uncertainty === 'object') {
        const cv = uncertainty.cv_percent;
        if (cv != null && Number.isFinite(cv) && cv > 0) {
            return `${formatted} ± ${cv.toFixed(1)}% IU/L`;
        }
        // Fallback to std from dict
        const std = uncertainty.std;
        if (std != null && Number.isFinite(std) && std > 0 && Math.abs(value) >= 1) {
            const pct = (std / Math.abs(value)) * 100;
            return `${formatted} ± ${pct.toFixed(1)}% IU/L`;
        }
        if (std != null && Number.isFinite(std) && std > 0) {
            return `${formatted} ± ${std.toFixed(2)} IU/L`;
        }
        return `${formatted} IU/L`;
    }

    // Legacy single-number uncertainty
    if (
        uncertainty === undefined
        || uncertainty === null
        || !Number.isFinite(uncertainty)
        || uncertainty <= 0
    ) {
        return `${formatted} IU/L`;
    }

    if (Math.abs(value) >= 1) {
        const pct = (uncertainty / Math.abs(value)) * 100;
        return `${formatted} ± ${pct.toFixed(1)}% IU/L`;
    }

    return `${formatted} ± ${uncertainty.toFixed(2)} IU/L`;
}
